import { isWithinDateRangePST } from '@utils/dateAndTime/isBetween';
import {
  DealsBannerAppearance,
  PdpDealsBannerEntry,
  PdpDealsBannerMatchRule,
  PdpDealsBannerResolverContext,
  ResolvedPdpDealsBanner,
} from '@adapters/prismic/types/pdpDealsBanner';

/** Delimiter used within a rule field to express OR across multiple values, e.g. "Arc'teryx, Patagonia". */
export const RULE_FIELD_DELIMITER = ',';

const DEFAULT_PRIORITY = 9999;
const DEFAULT_POSITION = 1;

const normalize = (value: string) => value.trim().toLowerCase();

const normalizeValues = (values?: string[]) =>
  (values ?? []).map(normalize).filter(Boolean);

const splitField = (value?: string | null): string[] =>
  value ? value.split(RULE_FIELD_DELIMITER).map(normalize).filter(Boolean) : [];

/**
 * Returns true when ALL populated fields in the rule match the context.
 * Comma-separated values within a field are OR'd.
 * A rule with no fields set never matches — use targetAllProducts for blanket coverage.
 */
const ruleMatchesContext = (
  rule: PdpDealsBannerMatchRule,
  context: PdpDealsBannerResolverContext,
  categoryTargets: string[]
): boolean => {
  const hasAnyField = [
    rule.brand,
    rule.category,
    rule.stickerType,
    rule.style,
    rule.variantSku,
    rule.productCode,
    rule.priceGroup,
  ].some((v) => v != null && v.trim() !== '');

  if (!hasAnyField) return false;

  const brandValues = splitField(rule.brand);
  const categoryValues = splitField(rule.category);
  const stickerTypeValues = splitField(rule.stickerType);
  const styleValues = splitField(rule.style);
  const variantSkuValues = splitField(rule.variantSku);
  const productCodeValues = splitField(rule.productCode);

  if (
    brandValues.length > 0 &&
    !brandValues.includes(normalize(context.brand ?? ''))
  ) {
    return false;
  }

  if (categoryValues.length > 0) {
    const normalizedTargets = normalizeValues(categoryTargets);
    const hasMatch = categoryValues.some((configuredValue) =>
      normalizedTargets.includes(configuredValue)
    );
    if (!hasMatch) return false;
  }

  if (
    stickerTypeValues.length > 0 &&
    !normalizeValues(context.stickerTypes).some((s) =>
      stickerTypeValues.includes(s)
    )
  ) {
    return false;
  }

  if (
    styleValues.length > 0 &&
    !styleValues.includes(normalize(context.style ?? ''))
  ) {
    return false;
  }

  if (
    variantSkuValues.length > 0 &&
    !variantSkuValues.includes(normalize(context.variantSku ?? ''))
  ) {
    return false;
  }

  if (
    productCodeValues.length > 0 &&
    !productCodeValues.includes(normalize(context.productCode ?? ''))
  ) {
    return false;
  }

  const priceGroupValues = splitField(rule.priceGroup);
  if (
    priceGroupValues.length > 0 &&
    !priceGroupValues.includes(normalize(context.activePriceGroup ?? ''))
  ) {
    return false;
  }

  return true;
};

export type RuleMatchResult = {
  rule: PdpDealsBannerMatchRule;
  matched: boolean;
};

export type DealsBannerMatchDiagnostics = {
  id: PdpDealsBannerEntry['id'];
  prismicId: PdpDealsBannerEntry['prismicId'];
  title: PdpDealsBannerEntry['title'];
  enabled: PdpDealsBannerEntry['enabled'];
  startDate: PdpDealsBannerEntry['startDate'];
  endDate: PdpDealsBannerEntry['endDate'];
  /** Configured slot (1 or 2) the banner renders into when it matches. */
  position: 1 | 2;
  /** Sort weight within the slot (lower renders first); drives the slot rank. */
  priority: number;
  targetAllProducts: boolean;
  isActive: boolean;
  matched: boolean;
  excludedByRule: boolean;
  includedByRule: boolean;
  includeRuleResults: RuleMatchResult[];
  excludeRuleResults: RuleMatchResult[];
};

const mapAppearanceToVariant = (
  appearance: DealsBannerAppearance
): ResolvedPdpDealsBanner['variant'] => {
  switch (appearance) {
    case 'yellow':
      return 'promos';
    case 'green':
      return 'membershipDiscount';
    case 'red':
      return 'dealsClearance';
    default:
      return 'promos';
  }
};

const isEntryActive = ({
  enabled,
  startDate,
  endDate,
}: Pick<PdpDealsBannerEntry, 'enabled' | 'startDate' | 'endDate'>) => {
  if (enabled === false) {
    return false;
  }

  return isWithinDateRangePST(startDate, endDate);
};

export const getDealsBannerMatchDiagnostics = (
  entry: PdpDealsBannerEntry,
  context: PdpDealsBannerResolverContext
): DealsBannerMatchDiagnostics => {
  const isActive = isEntryActive(entry);
  const categoryTargets = context.categoryTargets ?? [];

  const includeRuleResults: RuleMatchResult[] = (entry.includeRules ?? []).map(
    (rule) => ({
      rule,
      matched: ruleMatchesContext(rule, context, categoryTargets),
    })
  );

  const excludeRuleResults: RuleMatchResult[] = (entry.excludeRules ?? []).map(
    (rule) => ({
      rule,
      matched: ruleMatchesContext(rule, context, categoryTargets),
    })
  );

  const excludedByRule = excludeRuleResults.some((r) => r.matched);
  // At least one include rule must exist and match; no rules = no match (use targetAllProducts for blanket coverage)
  const includedByRule =
    includeRuleResults.length > 0 && includeRuleResults.some((r) => r.matched);

  const matched =
    isActive && !excludedByRule && (entry.targetAllProducts || includedByRule);

  return {
    id: entry.id,
    prismicId: entry.prismicId,
    title: entry.title,
    enabled: entry.enabled,
    startDate: entry.startDate,
    endDate: entry.endDate,
    position: entry.position ?? DEFAULT_POSITION,
    priority: entry.priority ?? DEFAULT_PRIORITY,
    targetAllProducts: Boolean(entry.targetAllProducts),
    isActive,
    matched,
    excludedByRule,
    includedByRule,
    includeRuleResults,
    excludeRuleResults,
  };
};

export const getMatchedDealsBanners = (
  entries: PdpDealsBannerEntry[],
  context: PdpDealsBannerResolverContext
): ResolvedPdpDealsBanner[] =>
  entries
    // Cheap enabled/date-window gate first: `matched` already requires
    // `isActive`, so inactive banners can never render — filtering them here
    // skips the expensive rule evaluation (some rules tokenize large
    // comma-separated strings, e.g. a ~700-entry style list) for banners that
    // are out of their date window.
    .filter((entry) => isEntryActive(entry))
    .filter((entry) => getDealsBannerMatchDiagnostics(entry, context).matched)
    .map((entry) => ({
      id: entry.id,
      priority: entry.priority ?? DEFAULT_PRIORITY,
      position: entry.position ?? DEFAULT_POSITION,
      variant: mapAppearanceToVariant(entry.appearance),
      title: entry.title,
      firstLineMarkdown: entry.firstLineMarkdown,
      descriptionMarkdown: entry.descriptionMarkdown,
      tooltipMarkdown: entry.tooltipMarkdown,
      tooltipPlacement: entry.tooltipPlacement,
      ctaLabel: entry.ctaLabel,
      ctaHref: entry.ctaHref,
    }))
    .sort(
      (left, right) =>
        left.position - right.position || left.priority - right.priority
    );

export const getResolvedDealsBanners = (
  entries: PdpDealsBannerEntry[],
  context: PdpDealsBannerResolverContext
): ResolvedPdpDealsBanner[] => {
  const resolvedEntries = getMatchedDealsBanners(entries, context);

  const countsByPosition = new Map<1 | 2, number>();

  return resolvedEntries.filter((entry) => {
    const currentCount = countsByPosition.get(entry.position) ?? 0;

    if (currentCount >= 2) {
      return false;
    }

    countsByPosition.set(entry.position, currentCount + 1);
    return true;
  });
};

/**
 * Further caps Prismic slot-1 banners based on how many code-driven slot-1 banners
 * are already being rendered (clearance and/or member-pricing). The hard cap for
 * slot 1 is 2 across code-driven + Prismic combined.
 */
export const applyCodeDrivenSlot1Cap = (
  slot1Banners: ResolvedPdpDealsBanner[],
  codeDrivenSlot1Count: number
): ResolvedPdpDealsBanner[] =>
  slot1Banners.slice(0, Math.max(0, 2 - codeDrivenSlot1Count));

export default getResolvedDealsBanners;
